import { omit, cloneDeep, isPlainObject } from 'lodash';

/**
 * Deeply omits properties from an object based on property names or dot-notation paths
 * @param obj The source object
 * @param props Property name(s) to omit at all levels or specific paths
 * @param options Configuration options
 * @returns A new object with the specified properties omitted
 */
export function omitDeep<T extends object>(
  obj: T,
  props: string | string[],
  options: { recursive?: boolean } = { recursive: true },
): T | Partial<T & object> | (T & object) {
  // Handle empty object
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // Convert single prop to array
  const propsArray = Array.isArray(props) ? props : [props];

  // Handle dot notation paths
  const pathProps = propsArray.filter((prop) => prop.includes('.'));
  const directProps = propsArray.filter((prop) => !prop.includes('.'));

  // Clone the object to avoid mutations
  let result: T | Partial<T & object> | (T & object) = cloneDeep(obj);

  // First handle any dot notation paths
  if (pathProps.length > 0) {
    for (const path of pathProps) {
      result = omitPath(result, path);
    }
  }

  // Then handle direct property names (potentially recursively)
  if (directProps.length <= 0) return result;

  // First omit at the top level
  result = omit(result, directProps);

  // If recursive, process all nested objects
  if (!options.recursive) return result;

  for (const key in result) {
    const value = result[key as keyof typeof result];
    if (value && isPlainObject(value)) {
      (result as any)[key] = omitDeep(value, directProps, {
        recursive: true,
      });
    } else if (Array.isArray(value)) {
      (result as any)[key] = value.map((item) =>
        isPlainObject(item) ? omitDeep(item, directProps, { recursive: true }) : item,
      );
    }
  }

  return result;
}

/**
 * Helper function to omit a property at a specific path
 */
function omitPath<T>(obj: T, path: string): T {
  const pathArray = path.split('.');

  if (pathArray.length === 0) return obj;

  const result = cloneDeep(obj);

  if (pathArray.length > 1) {
    const [first, ...rest] = pathArray;
    const restPath = rest.join('.');

    if (result && typeof result === 'object' && first in result) {
      const value = result[first as keyof T];
      if (value !== null && typeof value === 'object') {
        (result as any)[first] = omitPath(value, restPath);
      }
    }
    return result;
  }

  if (result && typeof result === 'object') {
    return omit(result, pathArray[0]) as T;
  }

  return result;
}
